// @deprecated — Use apiFetch/mlsFetch from ~/lib/fetchClient for new code.
// This module is kept for legacy superagent callers only.

import qs from "qs";
import request from "superagent";
import GlobalContainer from "~/components/global_container";
import { signOutAndRedirect as redirectToSignIn } from "~/lib/authSession";
import { refreshToken, stopBackgroundRefresh } from "~/lib/tokenManager";
import { getToken } from "~/lib/tokenStore";

const FORBIDDEN_MESSAGE = "You do not have permission to perform that action.";

const signOutAndRedirect = () => {
  redirectToSignIn({ stopRefresh: stopBackgroundRefresh });
};

// Wraps a superagent request with 401 retry logic.
// On 401: refresh token → create a fresh request via retryFactory → retry once.
// On retry failure: sign out and redirect.
// This patches .end(), which also covers .then()/await since superagent
// internally calls .end() from .then().
const withRetry = (req, retryFactory, retryLifecycle = {}) => {
  const originalEnd = req.end.bind(req);

  req.end = function (cb) {
    originalEnd((err, res) => {
      if (retryLifecycle.isRequestCurrent?.() === false) {
        return;
      }
      if (res?.status === 401) {
        if (retryLifecycle.canRefresh?.() === false) {
          return;
        }
        refreshToken()
          .then(() => {
            if (retryLifecycle.isRequestCurrent?.() === false) {
              return;
            }
            if (retryLifecycle.beforeRetry?.() === false) {
              return;
            }
            const retry = retryFactory();
            retryLifecycle.onRetry?.(retry);
            retry.end((error, response) => {
              if (retryLifecycle.isRequestCurrent?.() === false) {
                return;
              }
              cb(error, response);
            });
          })
          .catch(() => {
            if (retryLifecycle.isRequestCurrent?.() === false) {
              return;
            }
            signOutAndRedirect();
          });
      } else {
        if (res?.status === 403) {
          GlobalContainer.notify(FORBIDDEN_MESSAGE, "error");
        }
        cb(err, res);
      }
    });
    return req;
  };

  return req;
};

const APIRequest = {
  apiRequiredHeaders() {
    const headers = {};
    if (Rails.helpers.currentTeam) {
      headers.team = Rails.helpers.currentTeam.id;
    }
    const token = getToken();
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    return headers;
  },

  setDefaultOptions(options) {
    const defaultOpts = {
      data: {
        product: GlobalContainer?.product(),
      },
    };

    return _lodash.merge({}, defaultOpts, options);
  },

  ajaxRequest(resource, method, type = "application/json") {
    return request(method, `${Rails.apiUrl}/api${resource}`)
      .set(this.apiRequiredHeaders())
      .type(type)
      .accept("application/json");
  },

  get(options) {
    const mergedOptions = this.setDefaultOptions(options);
    const makeReq = () =>
      this.ajaxRequest(mergedOptions.resource, "GET").query(
        qs.stringify(mergedOptions.data, { arrayFormat: "brackets" }),
      );
    return withRetry(makeReq(), makeReq);
  },

  post(options, type, retryLifecycle) {
    const mergedOptions = this.setDefaultOptions(options);
    const makeReq = () =>
      this.ajaxRequest(mergedOptions.resource, "POST", type).send(
        mergedOptions.data,
      );
    return withRetry(makeReq(), makeReq, retryLifecycle);
  },

  put(options) {
    const mergedOptions = this.setDefaultOptions(options);
    const makeReq = () =>
      this.ajaxRequest(mergedOptions.resource, "PUT").send(mergedOptions.data);
    return withRetry(makeReq(), makeReq);
  },

  patch(options) {
    const mergedOptions = this.setDefaultOptions(options);
    const makeReq = () =>
      this.ajaxRequest(mergedOptions.resource, "PATCH").send(
        mergedOptions.data,
      );
    return withRetry(makeReq(), makeReq);
  },

  delete(options) {
    const mergedOptions = this.setDefaultOptions(options);
    const makeReq = () =>
      this.ajaxRequest(mergedOptions.resource, "DELETE").send(
        mergedOptions.data,
      );
    return withRetry(makeReq(), makeReq);
  },

  abort(req) {
    if (req) {
      req.abort();
    }
  },
};

export default APIRequest;
